import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useParams, useLocation } from 'react-router-dom';
import { Container, Modal, Button, Form, Tabs, Tab, Alert, Spinner } from 'react-bootstrap';

import { useTranslation } from 'react-i18next';
import dagre from 'dagre';

import {
  fetchCustomerNodeDefinitionAuditTrail,
  fetchWhereUsed,
  WhereUsedResult,
  checkNodeDefinitionParameterUsage,
  AffectedFlow,
} from '../../../../../redux/slices/customer_node_definitionsSlice';

// Tabs
import { DefinitionTab } from './components/tabs/DefinitionTab';
import { ParametersTab } from './components/tabs/ParametersTab';
import { PropertiesTab } from './components/tabs/PropertiesTab';
import AuditTrail from './components/tabs/AuditTrail';
import Documentation from './components/tabs/Documentation';
import { WaitEventsTab } from './components/tabs/WaitEventsTab';
import { RaiseEventsTab } from './components/tabs/RaiseEventsTab';
import ResponseMapping from './components/tabs/ResponseMapping';
import WhereUsed from './components/tabs/WhereUsed';

// Editors
import Bash from './components/editors/Bash';
import Flow from './components/editors/Flow';
import Rest from './components/editors/Rest';
import Sap from './components/editors/Sap';
import SapBw from './components/editors/Sapbw';
import Sapibp from './components/editors/Sapibp';
import SendMail from './components/editors/SendMail';
import Sql from './components/editors/Sql';
import SqlPlus from './components/editors/SqlPlus';
import TableRead from './components/editors/TableRead';
import WorkflowActionCreate from './components/editors/WorkflowActionCreate';
import WorkflowCreate from './components/editors/WorkflowCreate';
import Conditions from './components/editors/Conditions';
import NorscomPython from './components/editors/NorscomPython';

// Interfaces
import { UpdatedNode } from '../../../../../interfaces/nodeDefinitions';

// SCSS
import './NodeDefinitionEditor.scss';

import RemoveParameterModal from './components/modals/RemoveParameterModal';
import { observer } from 'mobx-react-lite';
import { EditModel } from 'edit/EditModel';
import { EditableNodeDefinition } from './edit/EditableNodeDefinition';
import { OutputMatcherTab } from './edit/OutputMatcherTab';
import { getNodeDefinition, type NodeDefinition } from 'api/node_definitions/endpoints/getNodeDefinition';
import { getNodeDefinitionGraph } from 'api/node_definitions/endpoints/getNodeDefinitionGraph';
import { toError } from 'helpers/toError';
import { notify } from 'messages';
import { acceptDegraded, received } from 'validation';
import { postNodeDefinition } from 'api/node_definitions/endpoints/postNodeDefinition';
import { putNodeDefinition } from 'api/node_definitions/endpoints/putNodeDefinition';
import { mapLoadedToWorking } from './edit/workingState';
import type { ExpandedSubflowState, FlowChildNode, FlowEdge, FlowState } from './edit/flowGraph';
import { Position } from 'reactflow';

const INITIAL_UPDATED_NODE = {
  restrict_to_scheduling_window: null,
  node_definition_id: '',
  description: '',
  config: {
    script: '',
    run_as_user: '',
    client: '',
    jobname: '',
    program_name: '',
    sap_system: '',
    variant: '',
  },
  name: '',
  default_queue_path: '',
  queue_path: '',
  parameters: [],
  child_nodes: [],
  child_edges: [],
  handles: [],
  type: '',
  sub_type: 'bash',
  zone_name: '',
  retention_policies: [],
  locks: [],
  auto_restart_enabled: false,
  auto_restart_amount: 0,
  auto_restart_delay_seconds: 0,
  restart_on_return_codes: [],
  hold_on_submit: false,
  runtime_limit_min_hhmmss: null,
  runtime_limit_max_hhmmss: null,
  runtime_limit_event: null,
  runtime_limit_terminate: false,
  folder: '',
};

const nodeDimsEditor = (n: FlowChildNode) => {
  const lbl = n.data?.label || '';
  const hnd = n.data?.handles || [];
  return {
    w: n.style?.width ?? Math.max(150, Math.max(lbl.length * 8 + 30, n.id.length * 7) + (hnd.length > 0 ? 85 : 0) + 20),
    h: n.style?.height ?? Math.max(55, 28 + (hnd.length || 0) * 27),
  };
};

const getDagrePositions = (nodes: FlowChildNode[], edges: FlowEdge[]): Record<string, { x: number; y: number }> => {
  const rootNodes = nodes.filter((n) => !n.parentNode && !n.hidden);
  if (!rootNodes.length) return {};
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    nodesep: 100,
    ranksep: 200,
    marginx: 50,
    marginy: 50,
  });
  rootNodes.forEach((n) => {
    const { w, h } = nodeDimsEditor(n);
    g.setNode(n.id, { width: w, height: h });
  });
  const rootIds = new Set(rootNodes.map((n) => n.id));
  edges.forEach((e) => {
    const src = String(e.source || '');
    const tgt = String(e.target || '');
    if (rootIds.has(src) && rootIds.has(tgt)) {
      try {
        g.setEdge(src, tgt);
      } catch {
        // dagre throws if a node is missing; skip this edge
      }
    }
  });
  dagre.layout(g);
  const result: Record<string, { x: number; y: number }> = {};
  rootNodes.forEach((n) => {
    const pos = g.node(n.id);
    if (pos) result[n.id] = { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 };
  });
  return result;
};

const HEADER = 35;
const GROUP_BUFFER = 60;

const getGroupChildLayout = (
  nodes: FlowChildNode[],
  edges: FlowEdge[],
  groupId: string
): {
  positions: Record<string, { x: number; y: number }>;
  width: number;
  height: number;
} => {
  const children = nodes.filter((n) => n.parentNode === groupId && !n.hidden);
  if (!children.length) return { positions: {}, width: 0, height: 0 };

  const allPositions: Record<string, { x: number; y: number }> = {};
  const sizedNodes = nodes.map((n) => {
    if (n.type !== 'subflowGroup' || n.parentNode !== groupId) return n;
    const nested = getGroupChildLayout(nodes, edges, n.id);
    Object.assign(allPositions, nested.positions);
    if (nested.width > 0)
      return {
        ...n,
        style: { ...n.style, width: nested.width, height: nested.height },
      };
    return n;
  });

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    nodesep: 80,
    ranksep: 120,
    marginx: GROUP_BUFFER,
    marginy: GROUP_BUFFER,
  });
  children.forEach((n) => {
    const sized = sizedNodes.find((s) => s.id === n.id) ?? n;
    const { w, h } = nodeDimsEditor(sized);
    g.setNode(n.id, { width: w, height: h });
  });
  const childIds = new Set(children.map((n) => n.id));
  edges.forEach((e) => {
    const src = String(e.source || '');
    const tgt = String(e.target || '');
    if (childIds.has(src) && childIds.has(tgt)) {
      try {
        g.setEdge(src, tgt);
      } catch {
        // dagre throws if a node is missing; skip this edge
      }
    }
  });
  dagre.layout(g);
  let maxX = 0;
  let maxY = 0;
  children.forEach((n) => {
    const pos = g.node(n.id);
    if (!pos) return;
    const x = pos.x - pos.width / 2;
    const y = pos.y - pos.height / 2 + HEADER;
    allPositions[n.id] = { x, y };
    maxX = Math.max(maxX, x + pos.width);
    maxY = Math.max(maxY, y + pos.height);
  });
  return {
    positions: allPositions,
    width: maxX + GROUP_BUFFER,
    height: maxY + GROUP_BUFFER,
  };
};

// Node types that produce an output file and therefore support output matchers (nap-backend OUTPUT_MATCHER_NODE_TYPES).
const OUTPUT_MATCHER_TYPES = new Set(['norscom_python', 'sap', 'sapbw', 'sapibp', 'shell', 'sqlplus']);

// Editors
const nodes = {
  shell: Bash,
  flow: Flow,
  sap: Sap,
  sapbw: SapBw,
  sapibp: Sapibp,
  rest: Rest,
  sendMail: SendMail,
  sql: Sql,
  sqlplus: SqlPlus,
  tableRead: TableRead,
  workflowActionCreate: WorkflowActionCreate,
  workflowCreate: WorkflowCreate,
  conditions: Conditions,
  norscom_python: NorscomPython,
};

// Determines which editor based on type
const Editor = ({ entity, state, setState, onChange, onArrayChange, onSubflowStateChange, onNavChange, onExpandNode, readOnly }: any) => {
  const { type } = state || {};

  if (!type) return <div>No type found</div>;

  const ComponentName = (nodes as any)[type];

  if (!ComponentName) return <div>No editor available for type: {type}</div>;

  return (
    <ComponentName
      entity={entity}
      state={state}
      setState={setState}
      onChange={onChange}
      onArrayChange={onArrayChange}
      onSubflowStateChange={onSubflowStateChange}
      onNavChange={onNavChange}
      onExpandNode={onExpandNode}
      readOnly={readOnly}
    />
  );
};

// Main editor component
const NodeDefinitionEditor = observer(() => {
  const { nodeId, name: nodeName } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const duplicateFromId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get('duplicateFromId');
    return raw ? Number(raw) : null;
  }, [location.search]);

  const readOnly = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get('view') === '1';
  }, [location.search]);

  useEffect(() => {
    const prev = document.title;

    const title = nodeId
      ? `Edit Node Definition - ${nodeId} - ${nodeName}`
      : duplicateFromId
        ? `Duplicate Node Definition - ${duplicateFromId}`
        : `New Node Definition`;

    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [nodeId, nodeName, duplicateFromId]);

  // Parameter names the user removed this edit whose flow usage they confirmed; read by the save callback and cleared after.
  const pendingRemoveParamNamesRef = useRef<string[]>([]);

  const model = useMemo(
    () =>
      new EditModel<EditableNodeDefinition, NodeDefinition | null>(
        async () => {
          if (nodeId) return mapLoadedToWorking(acceptDegraded(await getNodeDefinition(Number(nodeId))));
          if (duplicateFromId) return mapLoadedToWorking(acceptDegraded(await getNodeDefinition(duplicateFromId)));
          return null;
        },
        async (entity) => {
          try {
            const removeNames = pendingRemoveParamNamesRef.current;
            const body = { ...entity.toSave(), ...(removeNames.length ? { remove_parameter_names: removeNames } : {}) };
            const original = entity.original;
            // TODO(#1035, #1220): `acceptDegraded` reports an unreadable receipt as a failed save,
            // though the server has committed by then — and this editor stays OPEN after a
            // save, so the honest outcome is "out of sync: stop editing", not "it failed".
            // It also ACCEPTS a degraded receipt, where every other editor now refuses one.
            // Deliberately left as-is: both outcomes need the node-definition edit model, so
            // they land with that migration.
            const saved = acceptDegraded(
              original ? await putNodeDefinition({ zone_name: original.zoneName, name: original.name }, body) : await postNodeDefinition(body)
            );
            // Re-fetch via GET: the POST/PUT response is the thin upsert row and omits GET-only fields (editable_handles,
            // output_matchers, zone_id, …). Reinitializing from it would drop them — and a subsequent save would then persist
            // the dropped output_matchers as []. The GET returns the full, boundary-validated shape.
            // `received(...)`, not the out-of-sync arm: the re-fetch below throws if the
            // record cannot be read at all. It does NOT throw on a degraded one, so this
            // can still seed the form with a substitute — see the TODO above.
            return received(mapLoadedToWorking(acceptDegraded(await getNodeDefinition(saved.node_definition_id))));
          } finally {
            // Always clear, even on a failed save, so a later save never re-sends stale remove_parameter_names.
            pendingRemoveParamNamesRef.current = [];
          }
        },
        (loaded) => new EditableNodeDefinition(loaded, { duplicate: !!duplicateFromId }),
        { initialMode: nodeId ? 'edit' : 'create', successMessage: 'All changes have been saved!' }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeId, duplicateFromId]
  );

  const entity = model.entity;
  // Alias the model's working state to the names the (un-migrated) render body and subflow handlers already use, so they stay
  // unchanged: reads flow through entity.state (reactive under observer), writes route to entity.setState.
  const updatedNode: any = entity ? entity.state : INITIAL_UPDATED_NODE;
  const setUpdatedNode = useCallback((updater: any) => model.entity?.setState(updater), [model]);

  const [isInsideSubflow, setIsInsideSubflow] = useState(false);
  const [navSlot, setNavSlot] = useState<React.ReactNode>(null);
  const expandedNodesRef = useRef<Map<string, ExpandedSubflowState>>(new Map());
  const childNodesRef = useRef<FlowChildNode[]>([]);
  useEffect(() => {
    childNodesRef.current = updatedNode.child_nodes;
  }, [updatedNode.child_nodes]);

  const [activeTab, setActiveTab] = useState<string>('definition');
  // True while the Save handler runs its pre-save parameter-usage check, so the Save button is disabled before
  // model.submit() (which owns isSaving) takes over — prevents a double-click firing duplicate usage checks.
  const [isPreparingSave, setIsPreparingSave] = useState(false);

  // State for the remove parameter confirmation modal
  const [removedParamModal, setRemovedParamModal] = useState<{
    removedNames: string[];
    affectedFlows: AffectedFlow[];
  } | null>(null);

  const { name = '', description, config = {}, type } = updatedNode || {};

  const isEditMode = !!nodeId && !duplicateFromId;

  useEffect(() => {
    if (isEditMode && nodeId) {
      dispatch(fetchWhereUsed(Number(nodeId)));
    }
  }, [dispatch, isEditMode, nodeId]);

  const whereUsed = useAppSelector(
    (state) => (nodeId ? state?.customerNodeDefinitions.whereUsedById?.[Number(nodeId)] : undefined) as WhereUsedResult | undefined
  );
  // Count only structural references — things that would break if the
  // definition is changed or deleted. Instance history isn't a reference.
  const whereUsedCount =
    (whereUsed?.flows.length ?? 0) + (whereUsed?.triggers.length ?? 0) + (whereUsed?.events.length ?? 0) + (whereUsed?.integration_nodes.length ?? 0);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (model.entity?.isDirty) {
        e.preventDefault();
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [model]);

  const handleArrayChange = (e: any, index: any, arrayName = 'parameters') => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    if (type === 'checkbox') {
      newValue = checked;
    }

    if (index >= 0) {
      const copyItems = [...updatedNode[arrayName]];

      copyItems[index] = {
        ...copyItems[index],
        [name]: newValue,
      };

      return setUpdatedNode((prev: any) => ({
        ...prev,
        [arrayName]: [...copyItems],
      }));
    }
  };

  const handleChange = (e: any, isConfig = false) => {
    const { name, type, value, checked } = e.target;
    const normalized = type === 'checkbox' ? !!checked : value;

    if (isConfig) {
      const path = name.split('.');
      if (path.length === 2) {
        const [parentKey, childKey] = path;
        return setUpdatedNode((prev: any) => ({
          ...prev,
          config: {
            ...prev.config,
            [parentKey]: {
              ...prev.config?.[parentKey],
              [childKey]: normalized,
            },
          },
        }));
      }
      return setUpdatedNode((prev: any) => ({
        ...prev,
        config: { ...prev.config, [name]: normalized },
      }));
    }

    if (name === 'type') {
      return setUpdatedNode((prev: any) => ({
        ...prev,
        [name]: normalized,
        sub_type: normalized === 'shell' ? prev.sub_type || 'bash' : null,
      }));
    }

    return setUpdatedNode((prev: any) => ({
      ...prev,
      [name]: normalized,
    }));
  };

  const handleClose = () => {
    setTimeout(() => window.close(), 0);
  };

  const handleCancel = (e: any) => {
    e.preventDefault();
    // model.cancel confirms discard of unsaved changes before closing.
    void model.cancel(handleClose);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const ent = model.entity;
    if (!ent || model.isSaving || isPreparingSave) return;

    setIsPreparingSave(true);
    try {
      // When editing, warn if a parameter removed this edit is still referenced by flows, before saving.
      if (nodeId) {
        const currentNames = new Set(ent.parameters.map((p) => p.name));
        const removedNames = ent.originalParameterNames.filter((n) => !currentNames.has(n));
        if (removedNames.length > 0) {
          const usageResult: any = await dispatch(checkNodeDefinitionParameterUsage({ node_definition_id: Number(nodeId), parameter_names: removedNames }));
          const affectedFlows: AffectedFlow[] = usageResult?.payload?.affected_flows ?? [];
          if (affectedFlows.length > 0) {
            setRemovedParamModal({ removedNames, affectedFlows });
            return;
          }
        }
      }

      await model.submit();
    } finally {
      setIsPreparingSave(false);
    }
  };

  // Expand and collapse subflow
  const handleExpandNode = useCallback(
    async (nodeId: string) => {
      // Collapse
      const saved = expandedNodesRef.current.get(nodeId);
      if (saved) {
        const toCleanUp: string[] = [];
        expandedNodesRef.current.forEach((_, key) => {
          if (key !== nodeId && saved.childNodeIds.has(key)) {
            toCleanUp.push(key);
          }
        });
        toCleanUp.forEach((key) => expandedNodesRef.current.delete(key));

        setUpdatedNode((prev: FlowState) => {
          const isDescendant = (n: FlowChildNode): boolean => {
            if (!n.parentNode) return false;
            if (n.parentNode === nodeId) return true;
            const parent = prev.child_nodes.find((p) => p.id === n.parentNode);
            return parent ? isDescendant(parent) : false;
          };

          let newNodes = prev.child_nodes.filter((n) => n.id !== nodeId && !isDescendant(n)).concat(saved.originalNode);

          if (saved.ancestorOriginalStyles) {
            newNodes = newNodes.map((n) => {
              if (Object.prototype.hasOwnProperty.call(saved.ancestorOriginalStyles, n.id)) {
                return { ...n, style: saved.ancestorOriginalStyles[n.id] };
              }
              return n;
            });
          }

          const allEdgeIds = new Set(saved.childEdgeIds);
          toCleanUp.forEach((key) => {
            prev.child_edges.forEach((e) => {
              if (e.id.startsWith(`subflow-${key}__`)) allEdgeIds.add(e.id);
            });
          });

          const newEdges = prev.child_edges.filter((e) => !allEdgeIds.has(e.id));

          const groupChildPositions: Record<string, { x: number; y: number }> = {};
          const groupSizeOverrides: Record<string, { width: number; height: number }> = {};
          newNodes
            .filter((n) => n.type === 'subflowGroup')
            .forEach((g) => {
              const layout = getGroupChildLayout(newNodes, newEdges, g.id);
              Object.assign(groupChildPositions, layout.positions);
              if (layout.width > 0)
                groupSizeOverrides[g.id] = {
                  width: layout.width,
                  height: layout.height,
                };
            });
          const resizedNodes = newNodes.map((n) => {
            if (groupSizeOverrides[n.id])
              return {
                ...n,
                style: { ...n.style, ...groupSizeOverrides[n.id] },
              };
            return n;
          });
          const dagrePositions = getDagrePositions(resizedNodes, newEdges);
          const layoutedNodes = resizedNodes.map((n) => {
            if (groupChildPositions[n.id]) return { ...n, position: groupChildPositions[n.id] };
            if (dagrePositions[n.id]) return { ...n, position: dagrePositions[n.id] };
            return n;
          });

          return { ...prev, child_nodes: layoutedNodes, child_edges: newEdges };
        });

        expandedNodesRef.current.delete(nodeId);
        return;
      }

      // Expand
      const currentNodes = childNodesRef.current;
      const childNode = currentNodes?.find((n) => n.id === nodeId);
      const nodeDefId = childNode?.node_definition_id;

      if (!nodeDefId) {
        console.warn('[NodeDefinitionEditor] No node_definition_id for node:', nodeId);
        return;
      }

      try {
        const { child_nodes: fetchedChildren, child_edges: fetchedEdges } = acceptDegraded(await getNodeDefinitionGraph(nodeDefId));
        if (!fetchedChildren.length) return;

        const mappedChildren = fetchedChildren.map((node) => ({
          ...node,
          id: `${nodeId}__${node.id}`,
          node_definition_name: node.node_definition_name,
          zone_name: node.zone_name || node.node_definition_path?.split('|')[0],
          // Null when the placed definition no longer exists (a LEFT JOIN); react-flow then draws its default node.
          type: node.type ?? undefined,
          data: {
            label: node.label || node.node_definition_name,
            handles: node.handles || [],
            description: node.description,
            hasChildren: node.type === 'flow',
            draggable: false,
            submit_disabled: node.submit_disabled === true,
          },
          draggable: false,
        }));

        const mappedEdges = fetchedEdges.map((edge) => ({
          ...edge,
          id: `subflow-${nodeId}__${edge.id}`,
          source: `${nodeId}__${edge.source}`,
          target: `${nodeId}__${edge.target}`,
          type: edge.type || 'default',
        }));

        const childDagre = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
        childDagre.setGraph({
          rankdir: 'LR',
          nodesep: 80,
          ranksep: 120,
          marginx: 60,
          marginy: 60,
        });

        mappedChildren.forEach((node) => {
          const label = node.data?.label || '';
          const handles = node.data?.handles || [];
          // Also account for the ID string which renders below the label.
          // Compound IDs (node-1__node-2__node-3) are much wider than labels
          // and cause overlap when dagre only measures the label.
          const labelWidth = label.length * 8 + 30;
          const idWidth = node.id.length * 7;
          const handlesWidth = handles.length > 0 ? 85 : 0;
          const width = Math.max(150, Math.max(labelWidth, idWidth) + handlesWidth + 20);
          const height = Math.max(55, 28 + handles.length * 27);
          childDagre.setNode(node.id, { width, height });
        });

        mappedEdges.forEach((edge) => {
          childDagre.setEdge(edge.source, edge.target);
        });

        dagre.layout(childDagre);

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        mappedChildren.forEach((node) => {
          const pos = childDagre.node(node.id);
          const w = pos.width || 150;
          const h = pos.height || 55;
          minX = Math.min(minX, pos.x - w / 2);
          minY = Math.min(minY, pos.y - h / 2);
          maxX = Math.max(maxX, pos.x + w / 2);
          maxY = Math.max(maxY, pos.y + h / 2);
        });

        const PADDING = 60;
        const groupWidth = maxX - minX + PADDING * 2;
        const groupHeight = maxY - minY + PADDING * 2 + HEADER;

        const childrenWithPositions: FlowChildNode[] = mappedChildren.map((node) => {
          const pos = childDagre.node(node.id);
          const w = pos.width || 150;
          const h = pos.height || 55;
          return {
            ...node,
            parentNode: nodeId,
            extent: 'parent' as const,
            position: {
              x: pos.x - w / 2 - minX + PADDING,
              y: pos.y - h / 2 - minY + PADDING + HEADER,
            },
            targetPosition: Position.Left,
            sourcePosition: Position.Right,
          };
        });

        // Save original for restore
        const originalNode = childNodesRef.current.find((n) => n.id === nodeId);
        if (!originalNode) return;

        const childNodeIds = new Set(childrenWithPositions.map((n) => n.id));
        const childEdgeIds = new Set(mappedEdges.map((e) => e.id));

        const ANCESTOR_BUFFER = 60;
        const refNodes = childNodesRef.current;

        const ancestorOriginalStyles: Record<string, FlowChildNode['style']> = {};
        const ancestorResizes: Record<string, { width: number; height: number }> = {};

        {
          let currentId = nodeId;
          let currentW = groupWidth;
          let currentH = groupHeight;

          while (true) {
            const cur = refNodes.find((n) => n.id === currentId);
            const parentId = cur?.parentNode ?? null;
            if (!parentId) break;
            const parent = refNodes.find((n) => n.id === parentId);
            if (!parent) break;
            if (!ancestorOriginalStyles[parentId]) {
              ancestorOriginalStyles[parentId] = { ...(parent.style || {}) };
            }

            let childMaxX = 0;
            let childMaxY = 0;
            refNodes.forEach((n) => {
              if ((n.parentNode ?? null) !== parentId) return;
              const cx = n.position?.x ?? 0;
              const cy = n.position?.y ?? 0;
              const lbl = n.data?.label || '';
              const hnd = n.data?.handles || [];
              const w =
                n.id === currentId
                  ? currentW
                  : (n.style?.width ?? Math.max(150, Math.max(lbl.length * 8 + 30, n.id.length * 7) + (hnd.length > 0 ? 85 : 0) + 20));
              const h = n.id === currentId ? currentH : (n.style?.height ?? Math.max(55, 28 + hnd.length * 27));
              childMaxX = Math.max(childMaxX, cx + w);
              childMaxY = Math.max(childMaxY, cy + h);
            });
            ancestorResizes[parentId] = {
              width: childMaxX + ANCESTOR_BUFFER,
              height: childMaxY + ANCESTOR_BUFFER + HEADER,
            };
            currentId = parentId;
            currentW = ancestorResizes[parentId].width;
            currentH = ancestorResizes[parentId].height;
          }
        }

        expandedNodesRef.current.set(nodeId, {
          originalNode: { ...originalNode },
          childNodeIds,
          childEdgeIds,
          ancestorOriginalStyles,
        });

        setUpdatedNode((prev: FlowState) => {
          const allEdges = [...prev.child_edges, ...mappedEdges];
          const sized = prev.child_nodes.map((n) => {
            if (n.id === nodeId) {
              return {
                ...n,
                type: 'subflowGroup',
                draggable: true,
                style: { width: groupWidth, height: groupHeight },
                data: {
                  ...n.data,
                  isExpanded: true,
                  label: n.data?.label || n.label || originalNode?.name,
                },
              };
            }
            if (ancestorResizes[n.id]) {
              return { ...n, style: { ...n.style, ...ancestorResizes[n.id] } };
            }
            return n;
          });
          const withChildren = [...sized, ...childrenWithPositions];
          const changedGroups = new Set([nodeId, ...Object.keys(ancestorResizes)]);
          const groupChildPositions: Record<string, { x: number; y: number }> = {};
          const groupSizeOverrides: Record<string, { width: number; height: number }> = {};
          changedGroups.forEach((gid) => {
            const layout = getGroupChildLayout(withChildren, allEdges, gid);
            Object.assign(groupChildPositions, layout.positions);
            if (layout.width > 0)
              groupSizeOverrides[gid] = {
                width: layout.width,
                height: layout.height,
              };
          });
          const resizedNodes = withChildren.map((n) => {
            if (groupSizeOverrides[n.id])
              return {
                ...n,
                style: { ...n.style, ...groupSizeOverrides[n.id] },
              };
            return n;
          });
          const dagrePositions = getDagrePositions(resizedNodes, allEdges);
          const newNodes = resizedNodes.map((n) => {
            if (groupChildPositions[n.id]) return { ...n, position: groupChildPositions[n.id] };
            if (dagrePositions[n.id]) return { ...n, position: dagrePositions[n.id] };
            return n;
          });
          return {
            ...prev,
            child_nodes: newNodes,
            child_edges: allEdges,
          };
        });
      } catch (error) {
        notify.error(`Could not expand subflow ${nodeId}: ${toError(error).message}`);
      }
    },
    [setUpdatedNode]
  );

  const handleTabSelect = (tab: any) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    if (activeTab !== 'audit') return;
    if (!updatedNode.zone_name || !updatedNode.name) return;
    dispatch(
      fetchCustomerNodeDefinitionAuditTrail({
        zone_name: updatedNode.zone_name,
        node_definition_name: updatedNode.name,
      })
    );
  }, [activeTab, updatedNode.zone_name, updatedNode.name, dispatch]);

  if (model.isLoading) {
    return (
      <Container fluid className="editor-shell">
        <div className="p-5 text-center">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (model.loadError || !entity) {
    return (
      <Container fluid className="editor-shell">
        <Alert variant="danger" className="m-4">
          Failed to load node definition{model.loadError ? `: ${model.loadError.message}` : ''}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="editor-shell">
      <Form className="editor-form">
        <div className="editor-titlebar d-flex align-items-center gap-3">
          <h2 className="mb-0">
            {nodeId
              ? `Edit node ${nodeId} - ${name}`
              : duplicateFromId
                ? `Duplicate node from ${duplicateFromId} - ${name || ''}`
                : `New node definition - ${name || ''}`}
          </h2>
          {navSlot}
        </div>
        <Tabs defaultActiveKey="definition" id="bash-tabs" className="editor-tabs border-bottom" variant="underline" onSelect={handleTabSelect}>
          <Tab eventKey="definition" title="Definition">
            <DefinitionTab entity={entity} isEditMode={isEditMode}>
              <Editor
                entity={entity}
                state={updatedNode}
                setState={setUpdatedNode}
                onChange={handleChange}
                onArrayChange={handleArrayChange}
                onSubflowStateChange={setIsInsideSubflow}
                onNavChange={setNavSlot}
                onExpandNode={handleExpandNode}
                readOnly={readOnly}
              />
            </DefinitionTab>
          </Tab>

          <Tab eventKey="parameters" title="Parameters">
            <ParametersTab entity={entity} />
          </Tab>

          <Tab eventKey="wait-events" title="Wait Events">
            <WaitEventsTab entity={entity} />
          </Tab>

          <Tab eventKey="raise-events" title="Raise Events">
            <RaiseEventsTab entity={entity} />
          </Tab>

          {type === 'rest' && (
            <Tab eventKey="response" title="Response">
              <ResponseMapping state={updatedNode} setState={setUpdatedNode} />
            </Tab>
          )}

          {OUTPUT_MATCHER_TYPES.has(type) && (
            <Tab eventKey="output-matchers" title="Output matcher">
              <OutputMatcherTab entity={entity} />
            </Tab>
          )}

          <Tab eventKey="properties" title="Properties">
            <PropertiesTab entity={entity} state={updatedNode} onChange={handleChange} />
          </Tab>

          <Tab eventKey="audit" title="Audit trail">
            <AuditTrail nodeId={nodeId} />
          </Tab>

          <Tab eventKey="documentation" title="Documentation">
            <Documentation entity={entity} />
          </Tab>

          {isEditMode && nodeId && (
            <Tab
              eventKey="where-used"
              title={<span>Where used {whereUsedCount > 0 && <span className="badge bg-secondary ms-1">{whereUsedCount}</span>}</span>}
            >
              <WhereUsed nodeId={nodeId} />
            </Tab>
          )}
        </Tabs>

        {!isInsideSubflow && (
          <div className="editor-footer border-top d-flex">
            <Button variant="secondary" onClick={handleCancel}>
              Close
            </Button>
            {!readOnly && (
              <Button className="ms-2" onClick={handleSubmit} type="submit" variant="primary" disabled={model.isSaving || isPreparingSave}>
                Save
              </Button>
            )}
          </div>
        )}
      </Form>
      {removedParamModal && (
        <RemoveParameterModal
          removedNames={removedParamModal.removedNames}
          affectedFlows={removedParamModal.affectedFlows}
          onConfirm={async () => {
            pendingRemoveParamNamesRef.current = removedParamModal.removedNames;
            setRemovedParamModal(null);
            await model.submit();
          }}
          onCancel={() => setRemovedParamModal(null)}
        />
      )}
    </Container>
  );
});

export default NodeDefinitionEditor;
